import {FC, useEffect} from 'react';
import {useParams} from 'react-router';
import Markdown, {Components} from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {Link, styled, Typography} from '@mui/material';
import {useQuery} from '@tanstack/react-query';
import {addSaasGuideRoute} from 'src/routes';
import {getSetupGuide} from 'src/services/SetupGuideService';
import {cacheKeys} from 'src/services/shared/serviceConstants';
import {LoadingScreen} from 'src/components/LoadingScreen';
import {
  andyBlue,
  borderRadiusSmall,
  dividerColor,
  faintBlue,
  linkStyleFragment,
} from 'src/theme';

// Long-form Markdown in the Detexian look: the same white, Lato, 980px page
// the Contentful guides were rendered into, with spacing for lists, images
// and tables that rich text used to style for us.
const StyledGuideArticle = styled('article')(({theme}) => ({
  fontFamily: 'Lato, serif',
  color: 'hsl(0, 0%, 34%)',
  width: '100%',
  maxWidth: '980px',
  boxSizing: 'border-box',
  margin: 'auto',
  backgroundColor: 'white',
  padding: '20px 50px 20px 60px',
  lineHeight: 1.6,
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
  },

  'h1, h2, h3, h4': {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(1.5),
  },
  'h1': {color: andyBlue, fontWeight: 'bold'},
  'h2': {paddingBottom: theme.spacing(1), borderBottom: `1px solid ${dividerColor}`},
  'p': {marginTop: 0, marginBottom: theme.spacing(1.5)},
  'ol, ul': {paddingLeft: theme.spacing(3), marginBottom: theme.spacing(1.5)},
  'li + li': {marginTop: theme.spacing(0.75)},
  'li > p': {marginBottom: theme.spacing(0.75)},
  'img': {
    display: 'block',
    maxWidth: '100%',
    height: 'auto',
    margin: theme.spacing(1, 0),
    border: `1px solid ${dividerColor}`,
    borderRadius: borderRadiusSmall,
  },
  'code': {
    padding: '0 4px',
    fontSize: '0.9em',
    backgroundColor: 'hsl(0, 0%, 95%)',
    borderRadius: borderRadiusSmall,
  },
  'pre': {
    padding: theme.spacing(1.5),
    overflowX: 'auto',
    backgroundColor: 'hsl(0, 0%, 95%)',
    borderRadius: borderRadiusSmall,
    'code': {padding: 0, backgroundColor: 'transparent'},
  },
  'blockquote': {
    margin: theme.spacing(1.5, 0),
    paddingLeft: theme.spacing(2),
    borderLeft: `3px solid ${faintBlue}`,
    color: theme.palette.text.secondary,
  },
  'hr': {border: 0, borderTop: `1px solid ${dividerColor}`},
  'table': {width: '100%', borderCollapse: 'collapse', marginBottom: theme.spacing(1.5)},
  'th, td': {
    padding: theme.spacing(1, 1.5),
    border: `1px solid ${dividerColor}`,
    textAlign: 'left',
  },
  'th': {backgroundColor: 'hsl(0, 0%, 95%)', fontWeight: 'bold'},
}));

const markdownComponents: Components = {
  h1: ({children}) => <Typography variant="h3" component="h1">{children}</Typography>,
  h2: ({children}) => <Typography variant="h4" component="h2">{children}</Typography>,
  h3: ({children}) => <Typography variant="h5" component="h3">{children}</Typography>,
  h4: ({children}) => <Typography variant="h6" component="h4">{children}</Typography>,
  p: ({children}) => <Typography component="p">{children}</Typography>,
  // Assets are served from the same origin; open external links safely.
  a: ({href, children}) => {
    const isExternal = !!href && !href.startsWith('/') && !href.startsWith('#');
    return (
      <Link
        href={href}
        sx={linkStyleFragment}
        {...(isExternal ? {target: '_blank', rel: 'noopener noreferrer'} : {})}
      >
        {children}
      </Link>
    );
  },
};

export const SetupGuidePage: FC = () => {
  const {saasIdentifier} = useParams<{ saasIdentifier: string }>();
  document.title = 'Add SaaS Guide - Detexian';

  const {data, isLoading, error} = useQuery({
    queryKey: [cacheKeys.addSaaSGuides + saasIdentifier],
    queryFn: ({signal}) => getSetupGuide(saasIdentifier, signal),
    retry: false,
  });

  // A SaaS without its own guide falls back to the generic walkthrough.
  useEffect(() => {
    if (!error) return;
    console.error('No setup guide found for', saasIdentifier);
    window.open(`${addSaasGuideRoute}/${saasIdentifier}`, '_self');
  }, [error, saasIdentifier]);

  if (isLoading) return <LoadingScreen />;
  if (error || !data) return null;

  return (
    <StyledGuideArticle>
      <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {data}
      </Markdown>
    </StyledGuideArticle>
  );
};

export default SetupGuidePage;
